import { Component, Input, OnInit, booleanAttribute, numberAttribute } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { TwAforoViewModel } from '../aforos.types';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { CultivoService } from '../../../../_campanias/cultivos/cultivo.service';
import { CultivoViewModel } from '../../../../_campanias/cultivos/cultivo.types';
import { RecintoService } from '../../../../_campanias/recintos/recinto.service';
import { TwRecintoViewModel } from '../../../../_campanias/recintos/recinto.types';
import { AforoCultivoCamService } from '../../../../_campanias/unidades-inspeccion/aforo-cultivo.service';
import { CorteCultivoCamService } from '../../../../_campanias/unidades-inspeccion/corte-cultivo.service';
import { TwCorteCultivoCamViewModel, TwAforoCultivoCamViewModel } from '../../../../_campanias/unidades-inspeccion/unidad-inspeccion-datosbasicos.types';
import { MonthStringToNumberPipe } from '../../../../core/_infrastructure/classes/pipes';

@Component({
    selector: 'app-aforo-wrapper-edit',
    templateUrl: './aforo-wrapper-edit.component.html',
})
export class AforoWrapperEditComponent extends ComponentBase implements OnInit {
    @Input({ transform: numberAttribute }) id = 0; // Id del aforo
    @Input({ transform: booleanAttribute }) editMode = true;

    visibilitySpinner = false;
    visibilitySpinnerSave = false;

    aforo: TwAforoViewModel;
    formGroup: FormGroup;

    comunidadesAutonomas: Tc[];
    provincias: Tc[];
    recintos: TwRecintoViewModel[];
    cultivos: CultivoViewModel[];

    controlsDisabled = false;

    private successMessage = 'Aforo guardado correctamente';
    private listUrl = '/mantenimiento/aforos';
    private editionUrl = '/mantenimiento/aforo/edit/';

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getAforo(id);
        }
    });

    constructor(
        private monthStringToNumberPipe: MonthStringToNumberPipe,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private fb: FormBuilder,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private globalService: GlobalService,
        private aforoService: AforoCultivoCamService,
        private recintoService: RecintoService,
        private cultivoService: CultivoService,
        private corteCultivoCamService: CorteCultivoCamService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
        this.getListas();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Id: [null],
            IdComunidadAutonoma: [null, Validators.required],
            IdProvincia: [null, Validators.required],
            IdRecinto: [null, Validators.required],
            IdCultivo: [null, Validators.required],
            Anyo: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            Mes: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            Descripcion: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            KgsProduccionEstimada: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            KgsProduccionReal: [null, Validators.required],
        });
        this.form = this.formGroup;
    }

    private getAforo(id: number): void {
        this.visibilitySpinner = true;
        this.aforoService
            .getAforo(id)
            .pipe(
                tap((vm: TwAforoViewModel) => {
                    this.visibilitySpinner = false;
                    this.aforo = vm;
                    let cortes: TwCorteCultivoCamViewModel[];
                    this.corteCultivoCamService.getCortesByIdAforo(this.aforo.Id!).subscribe(res => {
                        cortes = res;
                        if (cortes.length > 0) {
                            this.disableControls();
                            this.controlsDisabled = true;
                        }
                    });
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    private disableControls(): void {
        this.form.controls['IdComunidadAutonoma'].disable();
        this.form.controls['IdProvincia'].disable();
        this.form.controls['IdRecinto'].disable();
        this.form.controls['IdCultivo'].disable();
        this.form.controls['Anyo'].disable();
        this.form.controls['Mes'].disable();
        this.form.controls['KgsProduccionReal'].disable();
    }

    private getListas(): void {
        this.loadComunidadesAutonomas();
    }

    private loadComunidadesAutonomas() {
        this.globalService.getComunidadesAutonomas().subscribe(res => {
            this.comunidadesAutonomas = res;
        });
    }

    onComunidadAutonomaSelected(comunidadAutonoma: number): void {
        if (comunidadAutonoma === null) {
            return;
        }
        this.globalService.getProvinciasByComunidad(comunidadAutonoma).subscribe(res => {
            this.provincias = res;
        });
    }

    onProvinciaSelected(provincia: number): void {
        if (provincia === null) {
            return;
        }
        this.recintoService.getRecintosByIdProvincia(provincia).subscribe(res => {
            this.recintos = res;
        });
    }

    onRecintoSelected(recinto: number): void {
        if (recinto === null) {
            return;
        }
        this.cultivoService.getAllCultivosByIdRecinto(recinto).subscribe(res => {
            this.cultivos = res;
        });
    }

    onSave(): void {
        this.visibilitySpinnerSave = true;
        const formRawValue = this.formGroup.getRawValue();

        const model: TwAforoCultivoCamViewModel = { ...formRawValue };
        if (!model.IdUsuario) {
            model.IdUsuario = this.authService.getLoggedInUser()?.Id || 0;
        }
        if (this.aforo?.FechaCreacion) {
            model.FechaCreacion = this.aforo?.FechaCreacion;
        }

        const mesSeleccionado = model.Mes.toString().charAt(0).toUpperCase() + model.Mes.toString().slice(1).toLowerCase();
        model.Mes = this.monthStringToNumberPipe.transform(mesSeleccionado)!;

        this.aforoService
            .saveAforo(model)
            .pipe(
                tap(res => {
                    this.visibilitySpinnerSave = false;
                    this.addSuccessMessage(this.successMessage);
                    if (!model.Id) {
                        this.gotoEdition(res);
                        return;
                    }
                    this.getAforo(model.Id);
                }),
                catchError(error => {
                    this.visibilitySpinnerSave = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private gotoEdition(IdAforo: number): void {
        this.router.navigate([this.editionUrl, IdAforo]);
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    get allowToSave(): boolean {
        return !this.formInvalid && this.editMode;
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
